import {
  clearWorkspaceAuthSession,
  getValidAccessToken,
  getWorkspaceAuthSession,
  hasWorkspaceAuthSession,
  isAccessTokenExpired,
  setWorkspaceAuthSession,
  type WorkspaceAuthSession,
} from '@amglobal/auth-shared'
import { clearCachedWorkspaceTenantLabel } from '@/features/auth/lib/session-display'
import { clearReceptionDeskSession } from '@/features/hr/lib/reception-desk-session'
import type { AuthResponse } from '../api/auth-types'
import {
  getDevEnvAccessToken,
  getDevPasteAccessToken,
  setWorkspaceAccessToken,
} from '@/shared/session/dev-access-token'

/** Resolves Bearer token: valid login session → env → pasted JWT. */
export function resolveWorkspaceAccessToken(): string | null {
  const fromSession = getValidAccessToken()
  if (fromSession) return fromSession

  const fromEnv = getDevEnvAccessToken()
  if (fromEnv) return fromEnv

  return getDevPasteAccessToken()
}

export function authResponseToSession(response: AuthResponse): WorkspaceAuthSession {
  return {
    accessToken: response.token,
    refreshToken: response.refreshToken,
    sessionId: response.sessionId,
    deviceId: response.deviceId,
    user: {
      id: response.user.id,
      email: response.user.email,
      userType: response.user.userType,
      roles: response.user.roles,
      capabilities: response.user.capabilities,
      sessionVersion: response.user.sessionVersion,
      isApplicantSealed: response.user.isApplicantSealed,
    },
  }
}

export function persistAuthResponse(response: AuthResponse): WorkspaceAuthSession {
  const session = authResponseToSession(response)
  setWorkspaceAuthSession(session)
  // Keep paste store in sync so DevTokenBar matches the live session after login.
  setWorkspaceAccessToken(session.accessToken)
  return session
}

export function clearWorkspaceSession(): void {
  clearReceptionDeskSession()
  clearWorkspaceAuthSession()
  clearCachedWorkspaceTenantLabel()
  setWorkspaceAccessToken(null)
}

export function readPersistedSession(): WorkspaceAuthSession | null {
  return getWorkspaceAuthSession()
}

export function isAuthenticated(): boolean {
  if (hasWorkspaceAuthSession()) return true
  return resolveWorkspaceAccessToken() != null
}

/** True when login material exists but the access JWT is expired (refresh may still work). */
export function hasExpiredAccessToken(): boolean {
  const session = getWorkspaceAuthSession()
  if (!session) return false
  return isAccessTokenExpired(session.accessToken)
}
